import { useState } from "react";
import { ClockIcon } from "@heroicons/react/24/outline";
import { useTheme } from "@hooks/useTheme";

const TimeSelector = ({
  value,
  onChange,
  placeholder,
  className,
  size = "md",
  "data-testid": dataTestId = "time-selector",
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  // Generate time options
  const hours = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString().padStart(2, '0'),
    label: i.toString().padStart(2, '0')
  }));

  const minutes = Array.from({ length: 60 }, (_, i) => ({
    value: i.toString().padStart(2, '0'),
    label: i.toString().padStart(2, '0')
  }));

  // Parse current value
  const [currentHour, currentMinute] = value ? value.split(':') : ['00', '00'];

  const handleHourChange = (hour) => {
    const newTime = `${hour}:${currentMinute}`;
    onChange(newTime);
  };

  const handleMinuteChange = (minute) => {
    const newTime = `${currentHour}:${minute}`;
    onChange(newTime);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} data-testid={dataTestId}>
      <div
        data-testid={`${dataTestId}-trigger`}
        className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none truncate cursor-pointer flex items-center justify-between ${
          colors.borderPrimary
        } ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
        onClick={toggleDropdown}
      >
        <span className={value ? colors.textPrimary : colors.textPlaceholder} data-testid={`${dataTestId}-value`}>
          {value || placeholder}
        </span>
        <ClockIcon className={`h-4 w-4 ${colors.textSecondary}`} />
      </div>

      {isOpen && (
        <div
          data-testid={`${dataTestId}-dropdown`}
          className={`absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-xl z-[9999] ${colors.borderPrimary} ${colors.bgPrimary} backdrop-blur-sm`}
        >
          <div className="p-3" data-testid={`${dataTestId}-dropdown-inner`}>
            <div className="flex gap-4">
              {/* Hours */}
              <div className="flex-1" data-testid={`${dataTestId}-hours`}>
                <label className={`block text-xs font-medium mb-2 ${colors.textSecondary}`}>Hours</label>
                <div className="max-h-32 overflow-y-auto">
                  {hours.map((hour) => (
                    <div
                      key={hour.value}
                      data-testid={`${dataTestId}-hour-${hour.value}`}
                      className={`px-2 py-1 cursor-pointer rounded text-sm transition-colors duration-150 ${
                        hour.value === currentHour
                          ? `${colors.textActive} border ${colors.dividerDashed} shadow-[0px_0px_2px_1px_rgba(0,0,0,0.6)]`
                          : `${colors.textPrimary} ${colors.hoverBorderDashed} ${colors.textHoverActive} hover:shadow-[0px_0px_2px_1px_rgba(0,0,0,0.6)]`
                      }`}
                      onClick={() => {
                        handleHourChange(hour.value);
                        closeDropdown();
                      }}
                    >
                      {hour.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Minutes */}
              <div className="flex-1" data-testid={`${dataTestId}-minutes`}>
                <label className={`block text-xs font-medium mb-2 ${colors.textSecondary}`}>Minutes</label>
                <div className="max-h-32 overflow-y-auto">
                  {minutes.map((minute) => (
                    <div
                      key={minute.value}
                      data-testid={`${dataTestId}-minute-${minute.value}`}
                      className={`px-2 py-1 cursor-pointer rounded text-sm transition-colors duration-150 ${
                        minute.value === currentMinute
                          ? `${colors.textActive} border ${colors.dividerDashed} shadow-[0px_0px_2px_1px_rgba(0,0,0,0.6)]`
                          : `${colors.textPrimary} ${colors.hoverBorderDashed} ${colors.textHoverActive} hover:shadow-[0px_0px_2px_1px_rgba(0,0,0,0.6)]`
                      }`}
                      onClick={() => {
                        handleMinuteChange(minute.value);
                        closeDropdown();
                      }}
                    >
                      {minute.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[10]"
          data-testid={`${dataTestId}-backdrop`}
          onClick={closeDropdown}
        />
      )}
    </div>
  );
};

export default TimeSelector; 